import React, { useRef, useState, useEffect } from 'react';

export interface SearchOption {
  value: string;
  label: string;
}

/**
 * Хайлттай сонголт (combobox). Жагсаалтыг `position: fixed`-ээр input-ийн
 * байрлалд харуулдаг тул modal-ийн overflow-д таслагдахгүй.
 *
 * Ижил төрлийн бүх modal-д (Тоот, оршин суугч г.м) дахин ашиглана.
 */
export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Хайх...',
  allowClear = true,
  clearLabel = '-- Сонгохгүй --',
  style,
}: {
  options: SearchOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  allowClear?: boolean;
  clearLabel?: string;
  style?: React.CSSProperties;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null);

  const selected = options.find(o => o.value === value);
  // Сонгосон утгыг харуулах (хайж байхгүй үед)
  useEffect(() => {
    if (!open) setQuery(selected ? selected.label : '');
  }, [value, open, selected]);

  const measure = () => {
    if (inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setRect({ top: r.bottom + 2, left: r.left, width: r.width });
    }
  };

  const q = query.toLowerCase().replace(/\s/g, '').trim();
  const matches = options.filter(o => o.label.toLowerCase().replace(/\s/g, '').includes(q));

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 14px', borderRadius: 10,
    border: '1px solid #d5d0c4', fontSize: 14, background: '#faf9f5',
    boxSizing: 'border-box', ...style,
  };

  return (
    <>
      <input
        ref={inputRef}
        value={query}
        onChange={e => { setQuery(e.target.value); if (value) onChange(''); measure(); setOpen(true); }}
        onFocus={() => { measure(); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        autoComplete="off"
        style={inputStyle}
      />
      {open && rect && (
        <div style={{
          position: 'fixed', top: rect.top, left: rect.left, width: rect.width,
          background: '#fff', border: '1px solid #d5d0c4', borderRadius: 8,
          maxHeight: 240, overflowY: 'auto', zIndex: 3000,
          boxShadow: '0 6px 18px rgba(0,0,0,.16)',
        }}>
          {allowClear && (
            <div
              onMouseDown={() => { onChange(''); setQuery(''); setOpen(false); }}
              style={{ padding: '9px 12px', cursor: 'pointer', color: '#6b7280', fontSize: 14 }}
            >{clearLabel}</div>
          )}
          {matches.length === 0 && (
            <div style={{ padding: '9px 12px', color: '#94a3b8', fontSize: 14 }}>Олдсонгүй</div>
          )}
          {matches.slice(0, 80).map(o => (
            <div
              key={o.value}
              onMouseDown={() => { onChange(o.value); setQuery(o.label); setOpen(false); }}
              style={{ padding: '9px 12px', cursor: 'pointer', fontSize: 14, background: o.value === value ? '#f0fdf4' : '#fff' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f4f1e8')}
              onMouseLeave={e => (e.currentTarget.style.background = o.value === value ? '#f0fdf4' : '#fff')}
            >{o.label}</div>
          ))}
        </div>
      )}
    </>
  );
}
